import React, { useState, useMemo } from 'react';
import { X, Merge, Check, Users, ArrowRight, AlertCircle, Plus, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * TableMergeModal - Merge tables together
 *
 * Two flows:
 * 1. From Available Table: Select other available tables → Pick master with PAX → Creates table group
 * 2. From Occupied Table: Initiating table is master → Select available tables to add
 *
 * Props:
 * - tables: All tables from the system
 * - initiatingTable: The table that started the merge (clicked merge button)
 * - mode: 'available' (merge available tables) or 'occupied' (add tables to occupied)
 * - onMerge: Callback with merge data
 * - onClose: Close modal
 * - isLoading: Loading state
 */
export default function TableMergeModal({
  tables,
  initiatingTable,
  mode = 'available',
  onMerge,
  onClose,
  isLoading
}) {
  const [selectedTableIds, setSelectedTableIds] = useState([]);
  const [masterTableId, setMasterTableId] = useState(
    mode === 'occupied' ? initiatingTable?.id : null
  );
  const [step, setStep] = useState(1); // 1: Select tables, 2: Select master (only for available mode)
  const [paxInput, setPaxInput] = useState('');
  const [showOtherSections, setShowOtherSections] = useState(false);

  // Get initiating table's section
  const initiatingSection = initiatingTable?.idSection || initiatingTable?.sectionId;

  // Get available tables grouped by section
  const { sameSectionTables, otherSectionTables } = useMemo(() => {
    const available = (tables || []).filter(table => {
      const status = (table.status || '').toLowerCase();
      return status === 'available' && table.isActive && table.id !== initiatingTable?.id;
    });

    const sameSection = available.filter(t =>
      (t.idSection || t.sectionId) === initiatingSection
    );
    const otherSections = available.filter(t =>
      (t.idSection || t.sectionId) !== initiatingSection
    );

    return { sameSectionTables: sameSection, otherSectionTables: otherSections };
  }, [tables, initiatingTable, initiatingSection]);

  // All available tables
  const availableTables = useMemo(() => {
    return [...sameSectionTables, ...otherSectionTables];
  }, [sameSectionTables, otherSectionTables]);

  // Group other section tables by section name
  const otherSectionGroups = useMemo(() => {
    const groups = {};
    otherSectionTables.forEach(table => {
      const sectionName = table.sectionName || table.section || 'Other';
      if (!groups[sectionName]) {
        groups[sectionName] = [];
      }
      groups[sectionName].push(table);
    });
    return groups;
  }, [otherSectionTables]);

  // Toggle table selection
  const toggleTable = (tableId) => {
    setSelectedTableIds(prev => {
      if (prev.includes(tableId)) {
        // If removing, also remove from master if it was master
        if (masterTableId === tableId) setMasterTableId(null);
        return prev.filter(id => id !== tableId);
      } else {
        return [...prev, tableId];
      }
    });
  };

  // For available mode, get all tables that will be merged (initiating + selected)
  const tablesToMerge = useMemo(() => {
    if (mode === 'occupied') {
      return availableTables.filter(t => selectedTableIds.includes(t.id));
    }
    // For available mode, include initiating table
    const selected = availableTables.filter(t => selectedTableIds.includes(t.id));
    return initiatingTable ? [initiatingTable, ...selected] : selected;
  }, [availableTables, selectedTableIds, initiatingTable, mode]);

  const masterTable = tablesToMerge.find(t => t.id === masterTableId);

  // Combined capacity
  const combinedCapacity = tablesToMerge.reduce((sum, t) => sum + (t.seatingCapacity || t.capacity || 0), 0);

  // Handle merge
  const handleMerge = () => {
    const pax = parseInt(paxInput) || 2;

    if (mode === 'occupied') {
      // Occupied mode: Add selected available tables to the occupied table
      if (selectedTableIds.length === 0) return;
      onMerge({
        tableIds: [initiatingTable.id, ...selectedTableIds],
        masterTableId: initiatingTable.id,
        mode: 'expand',
        peopleCount: pax,
      });
    } else {
      // Available mode: Merge available tables, pick master
      if (tablesToMerge.length < 2 || !masterTableId) return;
      onMerge({
        tableIds: tablesToMerge.map(t => t.id),
        masterTableId: masterTableId,
        mode: 'merge',
        peopleCount: pax,
      });
    }
  };

  // Validation
  const canProceedToStep2 = mode === 'available' && selectedTableIds.length >= 1;
  const canMerge = mode === 'occupied'
    ? selectedTableIds.length >= 1
    : tablesToMerge.length >= 2 && masterTableId && paxInput;

  // Theme - teal for all modes
  const theme = mode === 'occupied'
    ? {
        title: 'Expand Table',
        subtitle: 'Add available tables to your order',
        icon: Plus,
      }
    : {
        title: 'Merge Tables',
        subtitle: 'Combine tables for a larger group',
        icon: Merge,
      };

  const ThemeIcon = theme.icon;

  // Render table card
  const renderTableCard = (table, isSelectable = true) => {
    const isSelected = selectedTableIds.includes(table.id);

    return (
      <button
        key={table.id}
        onClick={() => isSelectable && toggleTable(table.id)}
        disabled={!isSelectable}
        className={`p-3 rounded-xl border-2 transition-all text-left ${
          isSelected
            ? 'border-teal-500 bg-teal-50 shadow-md'
            : 'border-slate-200 hover:border-teal-300 bg-white'
        } ${!isSelectable ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-bold text-slate-900">
            {table.name || table.tableNumber}
          </h4>
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
            isSelected
              ? 'bg-teal-600 border-teal-600'
              : 'border-slate-300 bg-white'
          }`}>
            {isSelected && <Check size={12} className="text-white" />}
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <Users size={12} />
          <span>{table.seatingCapacity || table.capacity || 0} seats</span>
        </div>
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="bg-teal-600 text-white px-5 py-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <ThemeIcon size={22} />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold">{theme.title}</h2>
            <p className="text-xs text-teal-100">{theme.subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded-lg transition"
          >
            <X size={18} />
          </button>
        </div>

        {/* Initiating Table Badge */}
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="px-3 py-1.5 rounded-lg font-bold text-sm bg-teal-100 text-teal-800 border border-teal-300">
              {initiatingTable?.name || initiatingTable?.tableNumber}
            </div>
            <span className="text-sm text-slate-600">
              {mode === 'occupied'
                ? '← Your table (orders will stay here)'
                : '← Starting table'
              }
            </span>
            {mode === 'available' && (
              <div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  step >= 1 ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-500'
                }`}>1</div>
                <span>Select</span>
                <ArrowRight size={12} className="text-slate-300" />
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  step >= 2 ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-500'
                }`}>2</div>
                <span>Master & PAX</span>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">

          {/* No available tables */}
          {availableTables.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle size={48} className="mx-auto text-amber-400 mb-3" />
              <p className="text-slate-600 font-medium">No available tables to merge</p>
              <p className="text-sm text-slate-500 mt-1">
                All other tables are currently occupied or reserved
              </p>
            </div>
          ) : (
            <>
              {/* Step 1: Select Tables */}
              {(step === 1 || mode === 'occupied') && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    {mode === 'occupied'
                      ? 'Select available tables to add to your current table:'
                      : 'Select additional tables to merge with:'
                    }
                  </p>

                  {/* Same Section Tables */}
                  {sameSectionTables.length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                        {initiatingTable?.sectionName || initiatingTable?.section || 'Same Section'}
                      </h3>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {sameSectionTables.map((table) => renderTableCard(table))}
                      </div>
                    </div>
                  )}

                  {/* Other Sections - Expandable */}
                  {otherSectionTables.length > 0 && (
                    <div className="border-t border-slate-200 pt-4">
                      <button
                        onClick={() => setShowOtherSections(!showOtherSections)}
                        className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition w-full"
                      >
                        {showOtherSections ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        <span>Other Sections ({otherSectionTables.length} tables)</span>
                      </button>

                      {showOtherSections && (
                        <div className="mt-3 space-y-4">
                          {Object.entries(otherSectionGroups).map(([sectionName, sectionTables]) => (
                            <div key={sectionName}>
                              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                                {sectionName}
                              </h4>
                              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                {sectionTables.map((table) => renderTableCard(table))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Summary for occupied mode */}
                  {mode === 'occupied' && selectedTableIds.length > 0 && (
                    <div className="mt-4 p-4 bg-teal-50 rounded-xl border border-teal-200">
                      <h4 className="text-sm font-semibold text-teal-900 mb-3">Expand Summary</h4>
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-bold text-teal-700">{selectedTableIds.length + 1}</p>
                          <p className="text-xs text-teal-600">Tables</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-teal-700">
                            {(initiatingTable?.seatingCapacity || initiatingTable?.capacity || 0) +
                              availableTables
                                .filter(t => selectedTableIds.includes(t.id))
                                .reduce((sum, t) => sum + (t.seatingCapacity || t.capacity || 0), 0)
                            }
                          </p>
                          <p className="text-xs text-teal-600">Total Capacity</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Select Master Table with PAX (available mode only) */}
              {step === 2 && mode === 'available' && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Choose the primary table and enter number of guests.
                  </p>

                  {/* Master Table Selection */}
                  <div>
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                      Select Primary Table
                    </h3>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {tablesToMerge.map((table) => {
                        const isMaster = masterTableId === table.id;

                        return (
                          <button
                            key={table.id}
                            onClick={() => setMasterTableId(table.id)}
                            className={`p-3 rounded-xl border-2 transition-all text-left ${
                              isMaster
                                ? 'border-emerald-500 bg-emerald-50 shadow-md'
                                : 'border-slate-200 hover:border-emerald-300 bg-white'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-bold text-slate-900">
                                {table.name || table.tableNumber}
                              </h4>
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                                isMaster ? 'bg-emerald-600' : 'bg-slate-100'
                              }`}>
                                {isMaster && <Check size={12} className="text-white" />}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-600">
                              <Users size={12} />
                              <span>{table.seatingCapacity || table.capacity || 0} seats</span>
                            </div>
                            {isMaster && (
                              <div className="mt-1.5 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded text-center">
                                PRIMARY
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* PAX Input */}
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Number of Guests (PAX)
                    </label>
                    <div className="flex gap-2 mb-3">
                      {[2, 4, 6, 8].map((num) => (
                        <button
                          key={num}
                          onClick={() => setPaxInput(num.toString())}
                          className={`flex-1 py-2 rounded-lg font-bold text-sm transition ${
                            paxInput === num.toString()
                              ? 'bg-teal-600 text-white'
                              : 'bg-white border border-slate-300 text-slate-700 hover:border-teal-400'
                          }`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                    <input
                      type="number"
                      value={paxInput}
                      onChange={(e) => setPaxInput(e.target.value)}
                      placeholder="Or enter custom number..."
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-center font-semibold"
                      min="1"
                      max={combinedCapacity}
                    />
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      Combined capacity: {combinedCapacity} seats
                    </p>
                  </div>

                  {/* Summary */}
                  <div className="p-4 rounded-xl border bg-teal-50 border-teal-200">
                    <h4 className="text-sm font-semibold mb-3 text-teal-900">Merge Summary</h4>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-xl font-bold text-teal-700">{tablesToMerge.length}</p>
                        <p className="text-xs text-teal-600">Tables</p>
                      </div>
                      <div>
                        <p className="text-xl font-bold text-teal-700">{combinedCapacity}</p>
                        <p className="text-xs text-teal-600">Capacity</p>
                      </div>
                      <div>
                        <p className="text-xl font-bold text-teal-700">{paxInput || '-'}</p>
                        <p className="text-xs text-teal-600">Guests</p>
                      </div>
                    </div>
                    {masterTable && (
                      <div className="mt-3 pt-3 border-t text-center border-teal-200">
                        <p className="text-xs text-teal-700">
                          Primary: <span className="font-bold">{masterTable.name || masterTable.tableNumber}</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-5 py-4 bg-slate-50 flex items-center gap-3">
          {mode === 'available' && step === 1 ? (
            <>
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 bg-white text-slate-700 rounded-lg font-semibold hover:bg-slate-100 transition border border-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={!canProceedToStep2}
                className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg font-bold hover:bg-teal-700 transition disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                Next
                <ArrowRight size={16} />
              </button>
            </>
          ) : mode === 'available' && step === 2 ? (
            <>
              <button
                onClick={() => setStep(1)}
                className="flex-1 px-4 py-2.5 bg-white text-slate-700 rounded-lg font-semibold hover:bg-slate-100 transition border border-slate-200"
              >
                Back
              </button>
              <button
                onClick={handleMerge}
                disabled={!canMerge || isLoading}
                className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg font-bold hover:bg-teal-700 transition disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Merging...
                  </>
                ) : (
                  <>
                    <Merge size={16} />
                    Merge Tables
                  </>
                )}
              </button>
            </>
          ) : (
            /* Occupied mode - single step */
            <>
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 bg-white text-slate-700 rounded-lg font-semibold hover:bg-slate-100 transition border border-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={handleMerge}
                disabled={selectedTableIds.length === 0 || isLoading}
                className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg font-bold transition disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Add Tables
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
